// Paged "you are here" row — pure layout, no DOM.
//
// One row of a position STACK (components/lenses/ScopePositionStack.jsx): the
// members of one dimension, ranked by activity, shown as a row of cells that
// FILLS the width. The point is a pagination cursor into a possibly-huge list:
//   • few members  → cells divide the row evenly (one member ⇒ one full cell);
//   • many members → cells hold a MINIMUM width and the row shows a WINDOW of
//     them around your index, paged via the cursor — so being #4,200 of 10,000
//     still lands you on a legible page instead of a 10,000-sliver smear.
// Cell WIDTH is uniform (a page slot); activity is carried by cell HEAT in the
// renderer, so magnitude survives without turning big members into overflow.

/**
 * @param {Array<{id}>} members            ranked members (any extra fields pass through)
 * @param {string} selectedId              you
 * @param {object} opts
 * @param {number} opts.width              row width px
 * @param {number} [opts.minCellW=64]      floor cell width → sets how many fit per page
 * @param {number|null} [opts.windowStart] page origin (index); null = center on you
 * @returns {{ cells: Array<{...m, x, w, index, selected}>, windowStart, visible, total, selectedIndex }}
 */
export function layoutPagedRow(members, selectedId, { width, minCellW = 64, windowStart = null } = {}) {
  const list = Array.isArray(members) ? members : [];
  const total = list.length;
  if (!(width > 0) || total === 0) {
    return { cells: [], windowStart: 0, visible: 0, total, selectedIndex: -1 };
  }
  const sid = String(selectedId);
  const selectedIndex = list.findIndex((m) => String(m.id) === sid);

  const capacity = Math.max(1, Math.floor(width / minCellW));
  const visible = Math.min(total, capacity);

  let start = windowStart;
  if (start == null) {
    // Default: window centered on you (clamped into range).
    start = selectedIndex >= 0 ? selectedIndex - Math.floor(visible / 2) : 0;
  }
  start = Math.max(0, Math.min(start, total - visible));

  const cellW = width / visible;
  const cells = [];
  for (let k = 0; k < visible; k++) {
    const index = start + k;
    const m = list[index];
    cells.push({ ...m, x: k * cellW, w: cellW, index, selected: index === selectedIndex });
  }
  return { cells, windowStart: start, visible, total, selectedIndex };
}

// Paging-cursor clamps — where the window jumps to when you page or click the
// cursor track, bounded to [0, total - visible] so it never scrolls off either
// end. Pure so both bounds are testable without the RibbonRow wiring.

/** Step the window one page in `dir` (±1), clamped. */
export function pageWindowStart(start, dir, visible, total) {
  return Math.max(0, Math.min((start ?? 0) + dir * visible, total - visible));
}

/** Jump so the window is centered on fraction `frac` (0…1) of the ranking, clamped. */
export function jumpWindowStart(frac, visible, total) {
  return Math.max(0, Math.min(Math.round(frac * total - visible / 2), total - visible));
}
